/**
 * <s-spin-wheel> — Spin-to-win discount popup
 * Opens its <dialog> after data-delay seconds unless the visitor already won or
 * dismissed it before (shown once per visitor). The dialog closes only through its close buttons
 * or Escape (no tap-outside close, so it can't vanish by accident on mobile).
 * Submitting the email form subscribes the visitor in the background, spins the
 * wheel to a weighted random segment and reveals the segment's discount code with a
 * one-click copy button. After winning, a floating tab reopens the code.
 */
const STORAGE_KEY = 'fazo-spin-wheel';
const SPIN_MS = 5200;

class SSpinWheel extends HTMLElement {
  #ac;
  #dialog;
  #timer;
  #won = null;
  #spinning = false;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const designMode = this.dataset.designMode === 'true';
    const state = this.#readState();

    if (state?.code) {
      this.#won = { label: state.label, code: state.code };
      this.#showResult(state.label, state.code);
      this.#toggleReopen(true);
    }

    // Show it once per visitor: never again after it was closed, skipped or won.
    if (designMode || !state) {
      const delay = Math.max(0, Number(this.dataset.delay || 0)) * 1000;
      this.#timer = window.setTimeout(() => this.#open(), delay);
    }

    // No <form> element: Shopify's bot protection hooks customer forms and submits them
    // natively, which reloads the page and closes the popup. A plain button keeps it open.
    const fields = this.querySelector('[data-spin-form]');
    const email = fields?.querySelector('input[type="email"]');
    fields?.querySelector('[data-spin-submit]')?.addEventListener('click', () => this.#onSubmit(fields), opts);
    email?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.#onSubmit(fields);
      }
    }, opts);
    email?.addEventListener('input', () => this.#toggleError(false), opts);

    // Never close while the wheel is spinning (e.g. Escape mid-spin).
    this.#dialog.addEventListener('cancel', (event) => {
      if (this.#spinning) event.preventDefault();
    }, opts);

    this.#dialog.addEventListener('close', () => {
      if (this.#won) this.#toggleReopen(true);
      else if (!designMode) this.#writeState({ dismissedAt: Date.now() });
    }, opts);

    this.querySelector('[data-spin-copy]')?.addEventListener('click', () => this.#copy(), opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    window.clearTimeout(this.#timer);
  }

  #open() {
    if (this.#dialog.open || document.querySelector('dialog[open]')) return;
    this.#dialog.showModal();
    // Remember right away, so leaving the page without closing also counts as seen.
    if (this.dataset.designMode !== 'true' && !this.#readState()) this.#writeState({ shownAt: Date.now() });
  }

  #toggleError(show) {
    this.querySelector('[data-spin-error]')?.classList.toggle('hidden', !show);
    this.querySelector('[data-spin-form] input[type="email"]')?.setAttribute('aria-invalid', String(show));
  }

  async #onSubmit(fields) {
    if (this.#spinning || this.#won) return;

    const email = fields.querySelector('input[type="email"]');
    const value = email?.value.trim() || '';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) {
      this.#toggleError(true);
      email?.focus();
      return;
    }
    this.#toggleError(false);

    const submit = fields.querySelector('[data-spin-submit]');
    if (submit) {
      submit.disabled = true;
      submit.textContent = submit.dataset.spinningLabel || submit.textContent;
    }
    email.blur();

    // Subscribe in the background; the spin never waits on or fails with the request.
    const body = new FormData();
    fields.querySelectorAll('input[name]').forEach((input) => body.append(input.name, input.value.trim()));
    fetch(fields.dataset.endpoint, {
      method: 'POST',
      body,
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
    }).catch(() => {});

    const segment = this.#pickSegment();
    if (!segment) return;

    this.#spinning = true;
    await this.#spinTo(Number(segment.dataset.index));
    this.#spinning = false;

    const label = segment.dataset.label || '';
    const code = segment.dataset.code || '';
    this.#won = { label, code };
    this.#writeState({ label, code, wonAt: Date.now() });
    this.#showResult(label, code);
    this.querySelector('[data-spin-result]')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  #pickSegment() {
    const segments = [...this.querySelectorAll('[data-spin-segment]')].filter((s) => s.dataset.code);
    const total = segments.reduce((sum, s) => sum + Math.max(0, Number(s.dataset.chance) || 0), 0);
    if (!segments.length) return null;
    if (total <= 0) return segments[Math.floor(Math.random() * segments.length)];

    let roll = Math.random() * total;
    for (const segment of segments) {
      roll -= Math.max(0, Number(segment.dataset.chance) || 0);
      if (roll < 0) return segment;
    }
    return segments[segments.length - 1];
  }

  #spinTo(index) {
    const rotor = this.querySelector('[data-spin-rotor]');
    if (!rotor) return Promise.resolve();

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const duration = reduce ? 400 : SPIN_MS;
    const jitter = Math.random() * 36 - 18;
    const turns = reduce ? 1 : 6;
    const angle = turns * 360 - (index * 60 + 30) + jitter;

    rotor.style.transition = `transform ${duration}ms cubic-bezier(0.17, 0.67, 0.12, 1)`;
    rotor.style.transform = `rotate(${angle}deg)`;

    return new Promise((resolve) => window.setTimeout(resolve, duration + 150));
  }

  #showResult(label, code) {
    this.querySelector('[data-spin-form-wrap]')?.classList.add('hidden');
    const result = this.querySelector('[data-spin-result]');
    if (!result) return;

    result.querySelectorAll('[data-spin-prize]').forEach((el) => { el.textContent = label; });
    const codeEl = result.querySelector('[data-spin-code]');
    if (codeEl) codeEl.textContent = code;

    const apply = result.querySelector('[data-spin-apply]');
    if (apply && code && !apply.dataset.ready) {
      const redirect = apply.getAttribute('href') || '/';
      apply.href = `${this.dataset.discountBase}${encodeURIComponent(code)}?redirect=${encodeURIComponent(redirect)}`;
      apply.dataset.ready = 'true';
    }

    result.classList.remove('hidden');
    result.classList.add('flex');
  }

  #toggleReopen(show) {
    const tab = this.querySelector('[data-spin-reopen]');
    if (!tab) return;
    tab.classList.toggle('hidden', !show);
    tab.classList.toggle('inline-flex', show);
  }

  async #copy() {
    const code = this.querySelector('[data-spin-code]')?.textContent?.trim();
    if (!code) return;

    let copied = false;
    try {
      await navigator.clipboard.writeText(code);
      copied = true;
    } catch {
      // Older mobile browsers: fall back to a temporary selection.
      const temp = document.createElement('textarea');
      temp.value = code;
      temp.setAttribute('readonly', '');
      temp.style.position = 'fixed';
      temp.style.opacity = '0';
      this.#dialog.append(temp);
      temp.select();
      try { copied = document.execCommand('copy'); } catch { copied = false; }
      temp.remove();
    }
    if (copied) this.#markCopied();
  }

  #markCopied() {
    const button = this.querySelector('[data-spin-copy]');
    const label = this.querySelector('[data-spin-copy-label]');
    const copyIcon = this.querySelector('[data-spin-copy-icon]');
    const checkIcon = this.querySelector('[data-spin-check-icon]');
    if (!button || !label) return;

    button.classList.add('is-copied');
    label.textContent = this.dataset.copiedLabel || label.textContent;
    copyIcon?.classList.replace('inline-flex', 'hidden');
    checkIcon?.classList.replace('hidden', 'inline-flex');

    window.clearTimeout(this.copyTimer);
    this.copyTimer = window.setTimeout(() => {
      button.classList.remove('is-copied');
      label.textContent = this.dataset.copyLabel || label.textContent;
      checkIcon?.classList.replace('inline-flex', 'hidden');
      copyIcon?.classList.replace('hidden', 'inline-flex');
    }, 2500);
  }

  #readState() {
    try {
      return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
    } catch {
      return null;
    }
  }

  #writeState(state) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      /* Storage unavailable: the popup may show again next visit. */
    }
  }
}

if (!customElements.get('s-spin-wheel')) customElements.define('s-spin-wheel', SSpinWheel);
